import { ArrowForward } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Typography
} from "@mui/material";
import moment from "moment-timezone";
import React, { useEffect, useLayoutEffect, useState } from "react";
import "react-international-phone/style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Logo } from "../../assets/images";
import {
  ALL_SIGNUP_PLANS,
  AUTOMATED_PRICES,
  LIVE_AUTO_PRICES,
  LIVE_PRICES,
  PricingAddons,
  PricingTier,
} from "../../components/subscription/pricing/pricingData";
import { PLAN_NAMES } from "../../constants/planNames";
import AccountVerification from "../../modals/AccountVerification";
import AuthService from "../../services/auth.service";
import chargebeeService from "../../services/chargebee.service";
import EventsLoggerService from "../../services/events-logger.service";
import { GhlService } from "../../services/ghl";
import { LeadsquaredService } from "../../services/leadsquared/leadsquared.service";
import { SocialProvider } from "../../types/auth";
import { Applications } from "../../types/enums";
import {
  buildCognitoHostedUiAuthorizeUrl,
  openCognitoSsoPopup,
} from "../../utils/cognitoSsoPopup";
import {
  sanitizeTextInput,
  validateAndSanitizeName,
  validateEmail,
} from "../../utils/common.util";
import {
  EVENT_TYPES,
  SIGNUP_FLOWS,
  SIGNUP_STEPS,
  logEvent,
} from "../../utils/events.util";
import { buildSsoOAuthStateString } from "../../utils/ssoRedirectState";
import SignupEntryStep from "./SignupEntryStep";
import SignupStepDetails from "./SignupStepDetails";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const MainSignup = () => {
  const navigate = useNavigate();

  const getTimezoneString = () => {
    const timezone = moment.tz.guess() || "UTC";
    const offset = moment.tz(timezone).format("Z");
    const zoneName = moment.tz(timezone).zoneName();
    return `(GMT${offset}),${timezone},${zoneName}`;
  };

  const [signupStep, setSignupStep] = useState<1 | 2>(1);
  const [showPromoField, setShowPromoField] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [leadSquaredId, setLeadSquaredId] = useState<string>("");
  const [ghlContactId, setGhlContactId] = useState<string>("");

  const generateFirstName = (email: string) => {
    const emailParts = email.split("@")[0];
    const name = emailParts
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
    return name;
  };

  const [formData, setFormData] = useState(() => {
    const tz = getTimezoneString();
    return {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      timezone: tz,
      subdomain: "",
    };
  });

  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    subdomain: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isAutoRegistering, setIsAutoRegistering] = useState(false);
  const [verificationData, setVerificationData] = useState({
    companyId: "",
    companyName: "",
    infLeadSquaredId: "",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [planDetails, setPlanDetails] = useState<any>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [noTrial, setNoTrial] = useState(false);
  const [monthlyPlanDetails, setMonthlyPlanDetails] = useState<any>(null);
  const [initialPlanId, setInitialPlanId] = useState<string | null>(null);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    discount: number;
    type: string;
  } | null>(null);
  const [promoError, setPromoError] = useState("");

  const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);

  /** Returns GHL tags for selected addons: easycrm-addons, paid-webinar-addons */
  const getAddonTagsForGhl = (): string[] => {
    const effectiveAddonIds = (calculateAllInOnePricing().addons ?? []).map(
      (a) => a.id,
    );
    const tags: string[] = [];
    if (effectiveAddonIds.includes("easycrm")) tags.push("easycrm-addons");
    if (effectiveAddonIds.includes("paidwebinars"))
      tags.push("paid-webinar-addons");
    return tags;
  };

  const planId = searchParams.get("planId");
  const cleanPlanId = planId ? planId.replace(/-\d+$/, "") : undefined;

  // Initialize billing period from URL
  const initializeBillingPeriodFromUrl = () => {
    if (cleanPlanId) {
      return cleanPlanId.includes("Yearly");
    }
    return true; // Default to yearly
  };

  const [isYearly, setIsYearly] = useState(initializeBillingPeriodFromUrl());

  // Update plan in URL when billing period changes
  const updatePlanInUrl = (newIsYearly: boolean) => {
    if (!planId) return;

    // Extract the attendee number from the current planId
    const attendeeMatch = planId.match(/-(\d+)$/);
    const attendeeNumber = attendeeMatch ? attendeeMatch[1] : "200"; // Default to 250 if no number found

    // Extract the base plan ID without period and attendee number
    const basePlanId = planId
      .replace(/(Monthly|Yearly)/g, "")
      .replace(/-\d+$/, "");
    const period = newIsYearly ? "Yearly" : "Monthly";
    const newPlanId = `${basePlanId}${period}-${attendeeNumber}`;

    setSearchParams(
      (prev) => {
        prev.set("planId", newPlanId);
        return prev;
      },
      { replace: true },
    );
  };

  const handleBillingPeriodChange = (newIsYearly: boolean) => {
    // If the user clicks on the already selected option, do nothing
    if (isYearly === newIsYearly) {
      return;
    }

    setIsYearly(newIsYearly);
    updatePlanInUrl(newIsYearly);

    // Swap the plan details without making API calls
    if (planDetails && monthlyPlanDetails) {
      if (newIsYearly) {
        // Switching to yearly: set planDetails to yearly, monthlyPlanDetails to monthly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains yearly data
        setMonthlyPlanDetails(planDetails); // planDetails contains monthly data
      } else {
        // Switching to monthly: set planDetails to monthly, monthlyPlanDetails to yearly
        setPlanDetails(monthlyPlanDetails); // monthlyPlanDetails contains monthly data
        setMonthlyPlanDetails(planDetails); // planDetails contains yearly data
      }
    }
  };

  // Set default planId if not present and track initial plan ID
  useEffect(() => {
    if (!planId) {
      // Use setSearchParams to update only the planId without reloading the page
      setSearchParams(
        (prev) => {
          prev.set("planId", "All-In-One-USD-Yearly-200");
          return prev;
        },
        { replace: true },
      );
    } else if (!initialPlanId) {
      // Set the initial plan ID only once
      setInitialPlanId(planId);
    }

    // Check if this is a free trial plan
    setIsTrial(planId === PLAN_NAMES.FREE_TRIAL);
  }, [planId, setSearchParams, initialPlanId]);

  // Store planId in localStorage when component mounts
  useEffect(() => {
    if (planId) {
      localStorage.setItem("planId", planId);
    }
  }, [planId]);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (!initialPlanId) return;

      const cleanInitialPlanId = initialPlanId.replace(/-\d+$/, "");
      setIsLoadingPlan(true);
      try {
        // Fetch both monthly and yearly plan details at once using clean plan IDs without numbers
        const basePlanId = cleanInitialPlanId.replace(/(Monthly|Yearly)/g, "");
        const monthlyPlanId = `${basePlanId}Monthly`;
        const yearlyPlanId = `${basePlanId}Yearly`;

        // Fetch both plans in parallel
        const [monthlyResponse, yearlyResponse] = await Promise.all([
          chargebeeService.getPlanById(monthlyPlanId),
          chargebeeService.getPlanById(yearlyPlanId),
        ]);

        // Store both plan details
        setPlanDetails(isYearly ? yearlyResponse.data : monthlyResponse.data);
        setMonthlyPlanDetails(
          isYearly ? monthlyResponse.data : yearlyResponse.data,
        );
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
        // Fallback: try to fetch just the current plan
        try {
          const response =
            await chargebeeService.getPlanById(cleanInitialPlanId);
          setPlanDetails(response.data);
        } catch (fallbackError) {
          console.error(
            "Failed to fetch fallback plan details:",
            fallbackError,
          );
        }
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPlanDetails();
  }, [initialPlanId]); // Only depend on initialPlanId, not planId

  useEffect(() => {
    // Extract plan details from planId and console log them
    if (planId) {
      // Parse the planId
      const parts = planId.split("-");
      let extractedPlanType: string;
      let currency: string;
      let duration: string;
      let extractedAttendees: number;

      if (parts[0] === "All") {
        extractedPlanType = PLAN_NAMES.ALL_IN_ONE; // "All-In-One"
        currency = parts[3]; // USD
        duration = parts[4]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[5]); // e.g., 750
      } else {
        extractedPlanType = parts[0] + "-" + parts[1]; // Could be LIVE_WEBINARS or AUTOMATED_GOLD
        currency = parts[2]; // USD
        duration = parts[3]; // Yearly/Monthly
        extractedAttendees = parseInt(parts[4]); // e.g., 250
      }

      // Validate that the extracted plan type is one of the valid plan names
      const validPlanTypes = [
        PLAN_NAMES.LIVE_WEBINARS,
        PLAN_NAMES.AUTOMATED_GOLD,
        PLAN_NAMES.ALL_IN_ONE,
      ];

      if (!validPlanTypes.includes(extractedPlanType)) {
        console.log(
          "Invalid plan type:",
          extractedPlanType,
          "redirecting to pricing page",
        );
        window.location.href = "https://easywebinar.com/pricing/";
        return;
      }

      // Get pricing data based on plan type
      let pricingData: PricingTier[];
      let selectedPlan: PricingTier | undefined;

      switch (extractedPlanType) {
        case PLAN_NAMES.LIVE_WEBINARS:
          pricingData = LIVE_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString(),
          );
          break;
        case PLAN_NAMES.AUTOMATED_GOLD:
          pricingData = AUTOMATED_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString(),
          );
          break;
        case PLAN_NAMES.ALL_IN_ONE:
          pricingData = LIVE_AUTO_PRICES;
          selectedPlan = pricingData.find(
            (plan: PricingTier) => plan.value === extractedAttendees.toString(),
          );
          break;
        default:
          console.log("Unknown plan type:", extractedPlanType);
          return;
      }

      if (!selectedPlan) {
        console.log(
          "No matching plan found for attendees:",
          extractedAttendees,
        );
        return;
      }
      setPlanPrice(
        duration === "Yearly"
          ? (selectedPlan.annual ?? 0)
          : (selectedPlan.monthly ?? 0),
      );
    }
  }, [planId]);

  const handlePhoneChange = (value: string) => {
    // Only update the state, don't do any processing
    setFormData((prev) => ({
      ...prev,
      phoneNumber: value,
    }));
  };

  const validatePassword = (password: string) => {
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
      password,
    );

    if (!hasMinLength) return "Password must be at least 8 characters";
    if (!hasUppercase) return "Password must contain an uppercase letter";
    if (!hasLowercase) return "Password must contain a lowercase letter";
    if (!hasNumber) return "Password must contain a number";
    if (!hasSpecialChar)
      return "Password must contain a special character (!@#...)";
    return "";
  };

  const validateForm = () => {
    const errors = {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      subdomain: "",
    };
    let isValid = true;

    // Check required fields with hyperlink injection protection
    if (!formData.firstName || !formData.firstName.trim()) {
      errors.firstName = "Name is required";
      isValid = false;
    } else {
      const firstNameValidation = validateAndSanitizeName(
        formData.firstName,
        "First name",
      );
      if (!firstNameValidation.isValid) {
        errors.firstName = firstNameValidation.error || "Invalid first name";
        isValid = false;
      } else if (formData.firstName.trim().length > 50) {
        errors.firstName = "First name must be 50 characters or less";
        isValid = false;
      }
    }

    // Last name validation with hyperlink injection protection
    if (formData.lastName && formData.lastName.trim()) {
      const lastNameValidation = validateAndSanitizeName(
        formData.lastName,
        "Last name",
        false,
      );
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error || "Invalid last name";
        isValid = false;
      } else if (formData.lastName.trim().length > 50) {
        errors.lastName = "Last name must be 50 characters or less";
        isValid = false;
      }
    }

    // Email validation - check if empty first, then validate format
    if (!formData.email || !formData.email.trim()) {
      errors.email = "Work email is required";
      isValid = false;
    } else {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || "Please enter a valid email";
        isValid = false;
      }
    }

    //Phone number validation
    // if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
    //   errors.phoneNumber = "Phone number is required";
    //   isValid = false;
    // } else if (formData.phoneNumber.trim().length > 20) {
    //   errors.phoneNumber = "Phone number must be 20 characters or less";
    //   isValid = false;
    // }

    // Password validation
    if (!formData.password.trim()) {
      errors.password = "Password is required";
      setPasswordError("Password is required");
      isValid = false;
    } else {
      const passwordValidationError = validatePassword(formData.password);
      if (passwordValidationError) {
        errors.password = passwordValidationError;
        setPasswordError(passwordValidationError);
        isValid = false;
      } else {
        setPasswordError("");
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // For subdomain field, convert to lowercase
    const processedValue = name === "subdomain" ? value.toLowerCase() : value;

    // Update the form data with the raw input value
    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Validate first name with hyperlink injection protection
    if (name === "firstName") {
      if (!value.trim()) {
        setFormErrors((prev) => ({
          ...prev,
          firstName: "Name is required",
        }));
      } else {
        const nameValidation = validateAndSanitizeName(value, "First name");
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({
            ...prev,
            firstName: nameValidation.error ?? "Invalid first name",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, firstName: "" }));
        }
      }
    }

    // Validate last name with hyperlink injection protection
    if (name === "lastName") {
      if (value.trim()) {
        const nameValidation = validateAndSanitizeName(
          value,
          "Last name",
          false,
        );
        if (!nameValidation.isValid) {
          setFormErrors((prev) => ({
            ...prev,
            lastName: nameValidation.error ?? "Invalid last name",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, lastName: "" }));
        }
      } else {
        setFormErrors((prev) => ({ ...prev, lastName: "" }));
      }
    }

    // Clear error when user starts typing (for other fields)
    if (name !== "firstName" && name !== "lastName") {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "password") {
      if (!value.trim()) {
        setPasswordError("Password is required");
      } else {
        setPasswordError(validatePassword(value));
      }
    }
  };

  const [verificationDialogOpen, setVerificationDialogOpen] =
    React.useState(false);

  const handleVerificationDialogOpen = () => {
    setVerificationDialogOpen(true);
  };

  const handleVerificationDialogClose = async () => {
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? "");
    // const leadData = {
    //   leadId: leadSquaredId,
    //   fields: [
    //     { Attribute: "mx_Plan_Name", Value: cleanPlanId },
    //     { Attribute: "mx_Status", Value: "Pending Verification" },
    //   ],
    // };
    // await LeadsquaredService.updateLsField(leadData);
    if (ghlContactId) {
      await GhlService.updateContactFields({
        contactId: ghlContactId,
        fields: [
          { key: "plan_name", value: fullPlanName },
          { key: "subscription_status", value: "In-Trial" },
        ],
      });
      await GhlService.addTags(ghlContactId, [fullPlanName]);
    }
    navigate("/login");
  };

  // Helper function to clear UTM params from localStorage
  const clearUtmParamsFromStorage = () => {
    localStorage.removeItem("utm_source");
    localStorage.removeItem("utm_medium");
    localStorage.removeItem("utm_campaign");
    localStorage.removeItem("utm_content");
    localStorage.removeItem("utm_term");
    localStorage.removeItem("ad_id");
    localStorage.removeItem("fbc_id");
    localStorage.removeItem("h_ad_id");
    localStorage.removeItem("utm_id");
    localStorage.removeItem("gc_id");
    localStorage.removeItem("h_ga_id");
    localStorage.removeItem("h_keyword_id");
    localStorage.removeItem("h_keyword");
    localStorage.removeItem("h_placement");
    localStorage.removeItem("g_special_campaign");
  };

  const handleVerificationDialogProceed = async () => {
    // handleVerificationDialogClose();
    setVerificationDialogOpen(false);
    const fullPlanName =
      planId === PLAN_NAMES.FREE_TRIAL || planId === "free-trial-plan"
        ? PLAN_NAMES.FREE_TRIAL
        : (planId ?? "");
    setIsLoading(true);
    try {
      const leadData = {
        leadId: leadSquaredId,
        fields: [
          {
            Attribute: "mx_Plan_Name",
            Value: isTrial ? PLAN_NAMES.FREE_TRIAL : cleanPlanId,
          },
          { Attribute: "mx_Status", Value: "Active" },
        ],
      };
      const requests: Promise<unknown>[] = [
        LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Email Verified",
        }),
        LeadsquaredService.updateLsField(leadData),
      ];
      if (ghlContactId) {
        requests.push(
          GhlService.addTags(ghlContactId, ["email verified", fullPlanName]),
        );
        const addonTags = getAddonTagsForGhl();
        if (addonTags.length)
          requests.push(GhlService.addTags(ghlContactId, addonTags));
        requests.push(
          GhlService.updateContactFields({
            contactId: ghlContactId,
            fields: [
              { key: "plan_name", value: fullPlanName },
              { key: "subscription_status", value: "In-Trial" },
            ],
          }),
        );
      }
      await Promise.all(requests);
    } catch (error) {
      console.error("Error updating lead data:", error);
    } finally {
      setIsLoading(false);
    }

    // Clear UTM parameters from localStorage after successful signup
    clearUtmParamsFromStorage();

    // Pass plan data through URL parameters
    const planData = {
      price: planPrice,
      currency: "USD",
      coupon: appliedPromo?.code || "",
      plan_name: planDetails?.name || cleanPlanId || "Unknown Plan",
      customer_type: "with_cc",
    };

    // Encode plan data as URL parameters
    const planDataParams = new URLSearchParams({
      ...planData,
      price: planData.price.toString(),
    }).toString();

    window.location.href = `/loading-dashboard?ew_email=${formData.email}&planId=${isTrial ? PLAN_NAMES.FREE_TRIAL : planId}&companyId=${encodeURIComponent(verificationData.companyId)}&showOnboarding=true&${planDataParams}`;
  };

  const handleSocialSignIn = (
    provider: SocialProvider,
    options?: { usePopup?: boolean },
  ) => {
    try {
      const planIdParam = searchParams.get("planId");
      const state = buildSsoOAuthStateString({
        provider,
        planIdParam,
        flow: "first-flow",
        usePreSignupFlow: true,
        popupFlow: options?.usePopup ?? false,
      });
      const authorizeUrl = buildCognitoHostedUiAuthorizeUrl({
        provider,
        state,
      });

      if (options?.usePopup) {
        openCognitoSsoPopup(authorizeUrl)
          .then(({ code, state: returnedState }) => {
            navigate(
              `/auth/sso/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(returnedState)}`,
            );
          })
          .catch((err: unknown) => {
            console.error("Cognito SSO popup error:", err);
            const message =
              err instanceof Error ? err.message : "Sign-in failed";
            if (message !== "Sign-in was cancelled") {
              toast.error(message);
            }
          });
        return;
      }

      window.location.href = authorizeUrl;
    } catch (error) {
      console.error("Google Sign In Error:", error);
      toast.error("Failed to initialize Google Sign In");
    }
  };

  const handleGoogleSignIn = () => handleSocialSignIn("Google");
  const handleMicrosoftSignIn = () => handleSocialSignIn("Microsoft");
  const handleLinkedinSignIn = () => handleSocialSignIn("Linkedin");

  const handleGetStarted = async (skipCard = false) => {
    let createdGhlContactId = "";
    try {
      setIsLoading(true);
      // Validation and display errors (from handleNext)
      const isValid = validateForm();
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      // Use firstName and lastName from form
      const firstName = (formData.firstName || "").trim();
      const lastName = (formData.lastName || "").trim();

      // Generate subdomain from first name (use form firstName if available, otherwise fallback to email for subdomain only)
      const baseSubdomain = (firstName || generateFirstName(formData.email))
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "");
      const [emailCheckResponse, subdomainResponse] = await Promise.all([
        AuthService.checkEmailExists(formData.email),
        AuthService.generateSubdomain(baseSubdomain, Applications.EASYWEBINAR),
      ]);
      if (emailCheckResponse.data?.exists) {
        toast.error(
          "An account with this email already exists. Please use a different email or try logging in.",
        );
        setIsLoading(false);
        return;
      }

      // Store the generated subdomain for use in the form
      setFormData((prev) => ({
        ...prev,
        subdomain: subdomainResponse.subdomain,
      }));

      // Log signup event (from handleNext)
      try {
        const planId = searchParams.get("planId");
        const eventData = {
          step: SIGNUP_STEPS.FORM,
          password: formData.password,
          planId: cleanPlanId || "",
          planUnits: planId ? planId.match(/-(\d+)$/)?.[1] || 10 : 10,
          signupFlow: SIGNUP_FLOWS.PRICING_PAGE,
        };

        logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
      } catch (error: any) {
        console.error("Error logging signup event:", error);
        // Don't block the flow if event logging fails
      }
      // let leadSquaredId = '';
      // Create lead in LeadSquared (from handleNext)
      try {
        let utm_Data = {
          utm_source: localStorage.getItem("utm_source"),
          utm_medium: localStorage.getItem("utm_medium"),
          utm_campaign: localStorage.getItem("utm_campaign"),
          utm_content: localStorage.getItem("utm_content"),
          utm_term: localStorage.getItem("utm_term"),
          ad_id: localStorage.getItem("ad_id"),
          fbc_id: localStorage.getItem("fbc_id"),
          h_ad_id: localStorage.getItem("h_ad_id"),
          utm_id: localStorage.getItem("utm_id"),
          gc_id: localStorage.getItem("gc_id"),
          h_ga_id: localStorage.getItem("h_ga_id"),
          h_keyword_id: localStorage.getItem("h_keyword_id"),
          h_keyword: localStorage.getItem("h_keyword"),
          h_placement: localStorage.getItem("h_placement"),
          g_special_campaign: localStorage.getItem("g_special_campaign"),
        };

        // Security: Sanitize input data before sending to LeadSquared
        const sanitizedFirstNameForLS = sanitizeTextInput(firstName);
        const sanitizedLastNameForLS = sanitizeTextInput(lastName);

        // const rawPhone = formData.phoneNumber ? sanitizeTextInput(formData.phoneNumber.trim()) : "";
        // const normalizedPhone = rawPhone.replace(/[\s()-]/g, "");
        // const phone = /^\+\d{1,4}$/.test(normalizedPhone) ? "" : rawPhone;

        const formDataWithDetails = {
          firstName: sanitizedFirstNameForLS, // Sanitized first name to prevent hyperlink injection
          lastName: sanitizedLastNameForLS, // Sanitized last name to prevent hyperlink injection
          email: formData.email.trim().toLowerCase(), // Normalize email
          password: formData.password,
          phoneNumber: "",
          timezone: getTimezoneString(),
          subdomain: subdomainResponse.subdomain,
        };

        // const [LsId, ghlContactId] = await Promise.all([
        //   LeadsquaredService.createtLead(formDataWithDetails, "", utm_Data),
        //   GhlService.createContact(formDataWithDetails, "", utm_Data),
        // ]);
        const ghlContactId = await GhlService.createContact(formDataWithDetails, "", utm_Data);
        // leadSquaredId = LsId as unknown as string;
        if (ghlContactId) {
          createdGhlContactId = ghlContactId;
          setGhlContactId(ghlContactId);
           GhlService.addTags(ghlContactId, ["cc trial user"]);
        }
        // const leadData = {
        //   leadId: leadSquaredId,
        //   fields: [
        //     { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
        //   ],
        // };
        // Promise.all([
        //   LeadsquaredService.joinActivity({
        //     id: leadSquaredId,
        //     activityEvent: 201,
        //     activityValue: "Signup Attempt - pricing",
        //   }),
        //   LeadsquaredService.updateLsField(leadData),
        // ]);
        // setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
      }

      // let cardTokenResult = null;
      const planId = searchParams.get("planId");

      // // Only process card if there's a plan and it's not a free trial and we're not skipping card collection
      // if (planId && !isTrial) {
      // Check if card has any validation errors
      // if (cardError) {
      //   toast.error("Please fix card validation errors before proceeding");
      //   setIsLoading(false);
      //   return;
      // }

      // const result = await cardRef.current.tokenize();

      // if (result.error) {
      //   setCardError(result.error.message || "Card tokenization failed");
      //   toast.error("Please check your card details.");
      //   setIsLoading(false);
      //   return;
      // }

      // cardTokenResult = result.token;
      // setCardToken(cardTokenResult);
      // setCardError(null);

      // if (!cardTokenResult) {
      //   setCardError("Please enter valid card details");
      //   toast.error("Please enter valid card details");
      //   setIsLoading(false);
      //   return;
      // }
      // }

      // Get UTM parameters directly from URL
      const urlParams = new URLSearchParams(window.location.search);

      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      // Remove null/undefined values from the UTM params object
      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Check if buyNow parameter is present and true
      const buyNow = urlParams.get("BuyNow") == "true";

      // Security: Sanitize input data before sending to API
      // Security: Sanitize names before sending to prevent hyperlink injection
      const sanitizedFirstName = sanitizeTextInput(formData.firstName.trim());
      const sanitizedLastName = sanitizeTextInput(
        formData.lastName?.trim() || "",
      );
      // const planAddons = calculateAllInOnePricing().addons ?? [];
      // const addonsPayload = isYearly ? planAddons.map((a) => ({id: a.chargeIdYearly, amount: 0})) : planAddons.map((a) => ({id: a.chargeIdmonthly, amount: 0}));
      const signupData = {
        ...formData,
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: sanitizedLastName, // Sanitized last name to prevent hyperlink injection
        email: formData.email.trim().toLowerCase(), // Normalize email
        subdomain: subdomainResponse.subdomain,
        // phoneNumber: formData.phoneNumber ? sanitizeTextInput(formData.phoneNumber.trim()) : "", // Sanitize phone
        phoneNumber: "",
        timezone: getTimezoneString(),
        // addOnItemPriceIds: addonsPayload,
        // ...(planId &&
        //   !isTrial &&
        //   !skipCard && {
        //   planId: planDetails.id,
        //   planUnits: parseInt(planId?.match(/-(\d+)$/)?.[1] || "1") || 1,
        //   cardToken: cardTokenResult,
        //   couponId: appliedPromo?.code,
        // }),
        ...(isTrial && {
          planId: planDetails.id,
        }),
        // Set trialEnd to 0 if buyNow is true
        ...(buyNow && { trialEnd: 0 }),
        // Include UTM parameters if they exist
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      const response = await AuthService.signupWithoutPayment(signupData);
      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });
      handleVerificationDialogOpen();
      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();
      toast.success("Signup successful! Please verify your account.");
      try {
        if (createdGhlContactId) {
          if (planDetails.id === "free-trial-plan") {
            await GhlService.addTags(createdGhlContactId, ["Free Plan"]);
          } else {
            await GhlService.addTags(createdGhlContactId, [
              isTrial ? PLAN_NAMES.FREE_TRIAL : (planId ?? ""),
            ]);
          }
          const addonTags = getAddonTagsForGhl();
          if (addonTags.length)
            await GhlService.addTags(createdGhlContactId, addonTags);
        }
        // await LeadsquaredService.joinActivity({
        //   id: leadSquaredId,
        //   activityEvent: 205,
        //   activityValue: "Waiting To Confirm Email Validation",
        // });
        // if (planDetails.id === "free-trial-plan") {
        //   await LeadsquaredService.joinActivity({
        //     id: leadSquaredId,
        //     activityEvent: 205,
        //     activityValue: "Free Plan",
        //   });
        // } else {
        //   await LeadsquaredService.joinActivity({
        //     id: leadSquaredId,
        //     activityEvent: 205,
        //     activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails.id,
        //   });
        //   await LeadsquaredService.joinActivity({
        //     id: leadSquaredId,
        //     activityEvent: 205,
        //     activityValue: "Currently In Trial",
        //   });
        // }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      setDomainSuggestions([]);
      // Clear UTM params on signup failure too
      clearUtmParamsFromStorage();
      // if (error.name === "INVALID_CARD") {
      //   setCardError("Please enter valid card details");
      //   toast.error("Invalid card details. Please try again.");
      // } else {
      //   toast.error(
      //     error.message || "An error occurred during signup. Please try again."
      //   );
      // }
    } finally {
      setIsLoading(false);
    }
  };

  // const validateCoupon = async () => {
  //   if (!promoCode.trim()) {
  //     setPromoError("Please enter a promo code");
  //     toast.error("Please enter a promo code");
  //     return;
  //   }

  //   try {
  //     const planId = searchParams.get("planId");
  //     const response = await chargebeeService.validateCoupon(
  //       promoCode,
  //       planId ? planId.replace(/-\d+$/, "") : undefined,
  //     );

  //     if (response.data.valid) {
  //       const { coupon } = response.data;
  //       setAppliedPromo({
  //         code: coupon.id,
  //         discount: coupon.discountPercentage || coupon.discountAmount,
  //         type: coupon.discountType,
  //       });
  //       setPromoError("");
  //       setShowPromoField(false);
  //       toast.success("Promo code applied successfully!");
  //     } else {
  //       setPromoError(response.data.error || "Invalid promo code");
  //       setAppliedPromo(null);
  //       toast.error(response.data.error || "Invalid promo code");
  //     }
  //   } catch (error: any) {
  //     console.error("Error validating coupon:", error);
  //     setPromoError("Failed to validate promo code");
  //     setAppliedPromo(null);
  //     toast.error("Failed to validate promo code. Please try again.");
  //   }
  // };

  // // Inside the Signup component, add a state variable for planFeatures
  // // const [planFeatures, setPlanFeatures] = useState<{
  // //   planType: string;
  // //   currency: string;
  // //   duration: string;
  // //   attendees: number;
  // //   liveAttendees?: number;
  // //   automatedAttendees?: number;
  // //   price: number | string;
  // //   webinars: number | string;
  // //   storage: string;
  // // } | null>(null);

  const [planPrice, setPlanPrice] = useState<number | string>("");

  function captureUTMParams() {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const email = urlParams.get("email");
    const adId = urlParams.get("ad_id");
    const fbcId = urlParams.get("fbc_id");
    const hAdId = urlParams.get("h_ad_id");
    const utmId = urlParams.get("utm_id");
    const gcId = urlParams.get("gc_id");
    const hGaId = urlParams.get("h_ga_id");
    const hKeywordId = urlParams.get("h_keyword_id");
    const hKeyword = urlParams.get("h_keyword");
    const hPlacement = urlParams.get("h_placement");
    const gSpecialCampaign = urlParams.get("g_special_campaign");

    if (utmSource) localStorage.setItem("utm_source", utmSource);
    if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
    if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
    if (utmContent) localStorage.setItem("utm_content", utmContent);
    if (utmTerm) localStorage.setItem("utm_term", utmTerm);
    if (adId) localStorage.setItem("ad_id", adId);
    if (fbcId) localStorage.setItem("fbc_id", fbcId);
    if (hAdId) localStorage.setItem("h_ad_id", hAdId);
    if (utmId) localStorage.setItem("utm_id", utmId);
    if (gcId) localStorage.setItem("gc_id", gcId);
    if (hGaId) localStorage.setItem("h_ga_id", hGaId);
    if (hKeywordId) localStorage.setItem("h_keyword_id", hKeywordId);
    if (hKeyword) localStorage.setItem("h_keyword", hKeyword);
    if (hPlacement) localStorage.setItem("h_placement", hPlacement);
    if (gSpecialCampaign)
      localStorage.setItem("g_special_campaign", gSpecialCampaign);
    // Set email from URL parameter if present
    if (email) {
      setFormData((prev) => ({
        ...prev,
        email: email,
      }));
    }
  }

  // Add useEffect for capturing UTM parameters
  useEffect(() => {
    captureUTMParams();
  }, []);

  /**
   * Auto-registration logic when profileId and email are present in URL parameters
   */
  useEffect(() => {
    const handleAutoRegistration = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const profileId = urlParams.get("profileId");
      const email = urlParams.get("email");

      if (profileId && email && !isLoadingPlan && planDetails) {
        try {
          setIsAutoRegistering(true);

          // Get static profile ID
          const staticProfileId = getStaticProfileId();

          // Check if the profileId matches our static ID
          if (profileId === staticProfileId) {
            // Call getLatestSignupEvent API
            const response =
              await EventsLoggerService.getLatestSignupEvent(email);

            if (response.data?.event?.event_data?.password) {
              const password = response.data.event.event_data.password;
              const planId = response.data.event.event_data.planId;
              const planUnits = 10; //response.data.event.event_data.planUnits;

              // Auto-fill the form
              setFormData((prev) => ({
                ...prev,
                email: email,
                password: password,
              }));

              // Generate firstName from email
              const firstName = generateFirstName(email);

              // Proceed with automatic registration using parsed planId and units
              await performAutoRegistration(
                email,
                password,
                firstName,
                planId,
                planUnits,
              );
            } else {
              console.error("Password not found in event data");
              toast.error("Unable to retrieve registration data");
            }
          } else {
            console.log(
              "Profile ID does not match, proceeding with normal flow",
            );
            console.log("Expected:", staticProfileId, "Received:", profileId);
          }
        } catch (error: any) {
          console.error("Error during auto-registration:", error);
          toast.error(
            "Auto-registration failed. Please proceed with manual signup.",
          );
        } finally {
          setIsAutoRegistering(false);
        }
      }
    };

    handleAutoRegistration();
  }, [isLoadingPlan, planDetails]);

  // Function to get static profile ID
  const getStaticProfileId = () => {
    const STATIC_PROFILE_ID = "550e8400-e29b-41d4-a716-446655440000";
    return STATIC_PROFILE_ID;
  };

  const calculateAllInOnePricing = () => {
    const defaultPlanId = "All-In-One-USD-Yearly-200";
    const currentPlanId = planId ?? defaultPlanId;
    const plan =
      ALL_SIGNUP_PLANS[currentPlanId] ?? ALL_SIGNUP_PLANS[defaultPlanId];

    return {
      planName: plan.planName,
      monthlyPrice: plan.monthly,
      annualPlanPerMonth: plan.annualPlanPerMonth,
      annualMaxPrice: plan.annualMaxPrice,
      liveAttendees: plan.liveAttendees,
      automatedAttendees: plan.automatedAttendees,
      savings: plan.savingsAmount,
      savingsPercentage: plan.savingsAmount
        ? (plan.savingsAmount / plan.annualMaxPrice) * 100
        : 0,
      totalPrice: isYearly ? plan.annualPlanPerMonth * 12 : plan.monthly,
      addons:
        PricingAddons?.filter((addon) => plan.addons?.includes(addon.id)) ?? [],
    };
  };

  // Function to perform automatic registration
  const performAutoRegistration = async (
    email: string,
    password: string,
    firstName: string,
    planId?: string,
    units?: number,
  ) => {
    let createdGhlContactId = "";
    try {
      setIsLoading(true);

      // Generate subdomain
      const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, "");
      const subdomainResponse = await AuthService.generateSubdomain(
        baseSubdomain,
        Applications.EASYWEBINAR,
      );

      // Create lead in LeadSquared
      const utm_Data = {
        utm_source: localStorage.getItem("utm_source"),
        utm_medium: localStorage.getItem("utm_medium"),
        utm_campaign: localStorage.getItem("utm_campaign"),
        utm_content: localStorage.getItem("utm_content"),
        utm_term: localStorage.getItem("utm_term"),
        ad_id: localStorage.getItem("ad_id"),
        fbc_id: localStorage.getItem("fbc_id"),
        h_ad_id: localStorage.getItem("h_ad_id"),
        utm_id: localStorage.getItem("utm_id"),
        gc_id: localStorage.getItem("gc_id"),
        h_ga_id: localStorage.getItem("h_ga_id"),
        h_keyword_id: localStorage.getItem("h_keyword_id"),
        h_keyword: localStorage.getItem("h_keyword"),
        h_placement: localStorage.getItem("h_placement"),
        g_special_campaign: localStorage.getItem("g_special_campaign"),
      };

      // Security: Sanitize input data before sending to API
      const sanitizedFirstName = sanitizeTextInput(firstName.trim());

      const formDataWithDetails = {
        firstName: sanitizedFirstName, // Sanitized first name to prevent hyperlink injection
        lastName: "",
        email: email.trim().toLowerCase(), // Normalize email
        password: password,
        timezone: getTimezoneString(),
        subdomain: subdomainResponse.subdomain,
      };

      try {
        const LsId = await LeadsquaredService.createtLead(
          formDataWithDetails,
          "",
          utm_Data,
        );
        const ghlContactId = await GhlService.createContact(
          formDataWithDetails,
          "",
          utm_Data,
        );
        if (ghlContactId) {
          createdGhlContactId = ghlContactId;
          setGhlContactId(ghlContactId);
          await GhlService.addTags(ghlContactId, ["cc trial user"]);
        }
        LeadsquaredService.joinActivity({
          id: LsId,
          activityEvent: 201,
          activityValue: "Auto Signup Attempt",
        });
        const leadData = {
          leadId: LsId,
          fields: [
            { Attribute: "mx_Source_lp", Value: searchParams.get("Source_IP") },
          ],
        };
        await LeadsquaredService.updateLsField(leadData);
        setLeadSquaredId(LsId);
      } catch (error: any) {
        console.error("Error creating lead:", error);
        return;
      }

      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utm_source: urlParams.get("utm_source"),
        utm_medium: urlParams.get("utm_medium"),
        utm_campaign: urlParams.get("utm_campaign"),
        utm_content: urlParams.get("utm_content"),
        utm_term: urlParams.get("utm_term"),
        ad_id: urlParams.get("ad_id"),
        fbc_id: urlParams.get("fbc_id"),
        h_ad_id: urlParams.get("h_ad_id"),
        utm_id: urlParams.get("utm_id"),
        gc_id: urlParams.get("gc_id"),
        h_ga_id: urlParams.get("h_ga_id"),
        h_keyword_id: urlParams.get("h_keyword_id"),
        h_keyword: urlParams.get("h_keyword"),
        h_placement: urlParams.get("h_placement"),
        g_special_campaign: urlParams.get("g_special_campaign"),
      };

      const cleanUtmParams = Object.entries(utmParams)
        .filter(([_, value]) => value !== null && value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // Prepare signup data
      const signupData = {
        ...formDataWithDetails,
        phoneNumber: "",
        ...(planId &&
          !isTrial && {
            planId: planId || cleanPlanId, // Use planId from event data or URL
            planUnits: units || 10, // Use parsed units from event data
            couponId: appliedPromo?.code,
          }),
        ...(isTrial && {
          planId: planId || cleanPlanId, // Use planId from event data if available
        }),
        ...(Object.keys(cleanUtmParams).length > 0 && {
          utm_params: cleanUtmParams,
        }),
      };

      // Perform signup
      const response = await AuthService.signup(signupData);

      setVerificationData({
        companyId: response.data.companyId || "",
        companyName: response.data.companyName || "",
        infLeadSquaredId: response.data.inf_lead_squared_id || "",
      });

      // Clear UTM params on successful signup
      clearUtmParamsFromStorage();

      // Join activities
      try {
        if (createdGhlContactId) {
          if (planDetails?.id === "free-trial-plan") {
            await GhlService.addTags(createdGhlContactId, ["Free Plan"]);
          }
        }
        await LeadsquaredService.joinActivity({
          id: leadSquaredId,
          activityEvent: 205,
          activityValue: "Waiting To Confirm Email Validation",
        });
        if (planDetails?.id === "free-trial-plan") {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Free Plan",
          });
        } else {
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: isTrial ? PLAN_NAMES.FREE_TRIAL : planDetails?.id,
          });
          await LeadsquaredService.joinActivity({
            id: leadSquaredId,
            activityEvent: 205,
            activityValue: "Currently In Trial",
          });
        }
      } catch (error: any) {
        console.error("Error joining activities:", error);
      }

      logEvent(email, EVENT_TYPES.SIGNUP, {
        step: SIGNUP_STEPS.VERIFICATION,
        planId: planId,
        planUnits: units,
        autoRegistration: true,
      });

      // Open verification dialog
      handleVerificationDialogOpen();
      toast.success(
        "Auto-registration successful! Please verify your account.",
      );
    } catch (error: any) {
      console.error("Auto-registration failed:", error);
      toast.error(
        error.message || "Auto-registration failed. Please try manual signup.",
      );
    } finally {
      setIsLoading(false);
    }
  };
  // Check for BuyNow parameter on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const buyNow = urlParams.get("BuyNow") === "true";

    setNoTrial(buyNow);
  }, []);

  return (
    <>
      {/* Auto-registration loading overlay */}
      {isAutoRegistering && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress size={60} sx={{ color: "white", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
            Setting up your account...
          </Typography>
          <Typography variant="body2" sx={{ color: "white", opacity: 0.8 }}>
            Please wait while we complete your registration
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          color: "#1f2937",
          fontFamily: "Inter",
          // backgroundColor:  signupStep === 1? "#fff" : "#f9fafb",
          backgroundImage:
            signupStep <= 2 ? "url(/images/signupBackground.webp)" : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            // backgroundColor: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: "16px 24px", sm: "24px 32px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={Logo}
              alt="logo"
              style={{ height: 24, cursor: "pointer", maxWidth: "100%" }}
              onClick={() => window.open("https://easywebinar.com/", "_blank")}
            />
          </Box>
          <Button
              variant="contained"
              endIcon={<ArrowForward />}
              size="small"
              onClick={() => navigate("/login")}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#4B4EB5" },
              }}
            >
              Login
            </Button>
          {/* {formData.email ? (
            <Box
              aria-label="signup-email-initial"
              sx={{
                height: 36,
                width: 36,
                borderRadius: "999px",
                bgcolor: "rgba(96, 102, 233, 0.12)",
                color: "#6066E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "0.95rem",
                userSelect: "none",
              }}
            >
              {(formData.email || "").trim().charAt(0).toUpperCase() || "U"}
            </Box>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              size="small"
              onClick={() => navigate("/login")}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#4B4EB5" },
              }}
            >
              Login
            </Button>
          )} */}
        </Box>
        {signupStep === 1 ? (
          <SignupEntryStep
            onEmailSignup={() => setSignupStep(2)}
            onGoogleSignIn={handleGoogleSignIn}
          />
        ) : signupStep === 2 ? (
          <SignupStepDetails
            formData={formData}
            formErrors={formErrors}
            handleInputChange={handleInputChange}
            handlePhoneChange={handlePhoneChange}
            isAutoRegistering={isAutoRegistering}
            isLoading={isLoading}
            onContinue={() => handleGetStarted(true)}
          />
        ) : null}
      </Box>
      <AccountVerification
        onClose={handleVerificationDialogClose}
        open={verificationDialogOpen}
        onProceed={handleVerificationDialogProceed}
        phoneNumber=""
        email={formData.email}
        password={formData.password}
        companyId={verificationData.companyId}
        companyName={verificationData.companyName}
        infLeadSquaredId={verificationData.infLeadSquaredId}
      />
    </>
  );
};
export default MainSignup;
